import React, { useState } from 'react';

import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Box, Grid, Theme, useTheme } from '@mui/material';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { get as _get } from 'lodash';

import { useAppDispatch } from '../../../app/hooks/hooks';
import { addDriver } from '../../../app/store/ClaimCreation/ClaimCreationSlice';
import {
  getDriverNames,
  getPolicyInsureds,
  getSelectedRiskPolicyNumber,
  userIsAdjuster,
} from '../../../app/store/combinedSelectors';
import {
  backBtn,
  cancelBtn,
  continueBtn,
  FNOLCancelConfirmationConstants,
  GenericModalTopMargin,
} from '../../../Constants/Constants';
import { Driver, DriverDefaults } from '../../../Constants/DriverConstants';
import { PostMessageCodes } from '../../../Constants/PostMessageCodes';
import { FIRST_NAME_REGEX, LAST_NAME_REGEX } from '../../../utils/Regex-Util';
import { postMessageToParentApp } from '../../../utils/Utilities';
import SingleSelect, { SingleSelectStyleInfo } from '../../post-fnol-auto/SingleSelect/SingleSelect';
import CallToAction from '../../shared/CallToAction/CallToAction';
import GenericModal from '../../shared/GenericModal/GenericModal';
import { RegexTextField } from '../../shared/RegexTextField/RegexTextField';

export default function DriverSelection() {
  const theme: Theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const selectedPolicyNumber = useSelector(getSelectedRiskPolicyNumber);
  const driverOptions = useSelector(getDriverNames);
  const insureds = useSelector(getPolicyInsureds);
  const isAdjuster = useSelector(userIsAdjuster);
  const [driver, setDriver] = React.useState('');
  const [firstName, setFirstName] = React.useState('');
  const [lastName, setLastName] = React.useState('');
  const [showNameInput, setShowNameInput] = React.useState<boolean>(false);
  const [cancelConfirmationModalOpen, setCancelConfirmationModalOpen] = useState(false);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Filter list of drivers with selected policy number
  const filteredDriversList = driverOptions.filter((driver) => {
    if (_get(driver, 'policyNumber')) {
      return driver.policyNumber === selectedPolicyNumber;
    }
    return driver;
  });

  const themeBasedStyleInfo: SingleSelectStyleInfo = {
    headerStyle: theme.custom.styles.titleType4,
    descriptionStyle: theme.custom.styles.primaryDescription,
    optionStyle: {
      ...theme.custom.styles.primaryDescription,
      radioButtonColor: theme.custom.colors.radioButtonColor,
      radioButtonSelectedColor: theme.custom.colors.radioButtonSelectedColor,
      borderColor: theme.custom.styles.showBorderAroundListItems
        ? `${theme.custom.styles.listItemBorderDefaultStyle} ${theme.custom.styles.radioandcheckboxStyle.defaultBorder}`
        : theme.custom.colors.bodyColor,
      selectedBorderColor: `${theme.custom.styles.listItemBorderSelectedStyle} ${theme.custom.styles.radioandcheckboxStyle.selectedBorder}`,
      borderRadius: theme.custom.styles.borderRadius.radiusDefault,
      radioButtonSize: theme.custom.styles.defaultRadioButtonSize,
      optionHeight: theme.custom.styles.defaultInputHeight,
    },
  };

  function handleNext() {
    let selectedDriver: Driver | undefined;
    if (driver === DriverDefaults.DRIVER_NOT_ON_POLICY) {
      selectedDriver = {
        id: DriverDefaults.DRIVER_NOT_ON_POLICY,
        firstName: firstName,
        lastName: lastName,
      };
    } else if (driver === DriverDefaults.NO_DRIVER_IN_VEHICLE) {
      selectedDriver = {
        id: DriverDefaults.NO_DRIVER_IN_VEHICLE,
        firstName: '',
        lastName: '',
      };
    } else {
      const selctedInsured = insureds.find(
        (insured) => insured.id === driver && insured.policyNumber === selectedPolicyNumber
      );
      selectedDriver = {
        id: selctedInsured.id,
        firstName: selctedInsured.firstName,
        lastName: selctedInsured.lastName,
      };
    }
    dispatch(addDriver({ driverInfo: selectedDriver }));
    navigate('/time-location-selection');
  }

  function handleSingleSelectionChange(id: string) {
    setDriver(id);
    if (id === DriverDefaults.DRIVER_NOT_ON_POLICY) {
      setShowNameInput(true);
    } else {
      setShowNameInput(false);
    }
  }

  function handleFirstNameChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const newFirstName = _get(event, 'target.value');
    setFirstName(newFirstName);
  }

  function handleLastNameChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const newLastName = _get(event, 'target.value');
    setLastName(newLastName);
  }

  function handleBack() {
    if (isAdjuster) {
      navigate('/confirm-insureds');
    } else {
      navigate('/risk-selection');
    }
  }

  function handleConfirmedCancel() {
    navigate('/claims-overview');
  }

  function handleCancel() {
    if (isMobile && theme.custom.styles.showModalPopUpOnFNOLCancel) {
      postMessageToParentApp(PostMessageCodes.DIGITAL_CLAIM_V2_SCROLL);
      setCancelConfirmationModalOpen(true);
    } else {
      handleConfirmedCancel();
    }
  }

  function handleCloseButtonClick() {
    setCancelConfirmationModalOpen(false);
  }

  return (
    <Box sx={{ width: '100%' }}>
      {!!cancelConfirmationModalOpen && (
        <GenericModal
          title={FNOLCancelConfirmationConstants.TITLE}
          description={FNOLCancelConfirmationConstants.DESCRIPTION}
          primaryButtonText={FNOLCancelConfirmationConstants.PRIMARY_BUTTON_TEXT}
          secondaryButtonText={FNOLCancelConfirmationConstants.SECONDARY_BUTTON_TEXT}
          verticalPositionForMobile={GenericModalTopMargin}
          alertBoxStyle={theme.custom.styles.genericModalCancelAlertBoxStyle}
          theme={theme}
          closeButtonActionHandler={handleCloseButtonClick}
          primaryButtonActionHandler={handleConfirmedCancel}
          secondaryButtonActionHandler={handleCloseButtonClick}
        ></GenericModal>
      )}
      <Grid
        container
        sx={{
          width: '100%',
          maxWidth: '1280px',
          minWidth: '310px',
          margin: '0 auto',
          marginTop: '1em',
        }}
      >
        <Grid item xs={12}>
          <SingleSelect
            selectionChangeHandler={handleSingleSelectionChange}
            styleInfo={themeBasedStyleInfo}
            options={filteredDriversList}
            description="Please select who was driving the vehicle at the time of the incident."
            header={'Who was the driver?'}
          ></SingleSelect>
        </Grid>
        <Grid sx={{ marginTop: '1em' }} item xs={12}>
          {showNameInput && (
            <Grid container spacing={2}>
              <Grid item sx={{ marginTop: '1rem' }} xs={12}>
                <Typography sx={theme.custom.styles.titleType4}>What is the driver’s name?</Typography>
              </Grid>
              <Grid container item id="first name" xs={12} md={6}>
                <Grid item xs={12}>
                  <Typography sx={theme.custom.styles.formLabelStyle}>First Name</Typography>
                </Grid>
                <Grid item xs={12}>
                  <RegexTextField
                    size="small"
                    id="firstname"
                    value={firstName}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    regex={FIRST_NAME_REGEX}
                    changeHandler={handleFirstNameChange}
                    inputProps={{
                      step: 300, // 5 min
                    }}
                    sx={{ width: '100%' }}
                    variant="outlined"
                  />
                </Grid>
              </Grid>
              <Grid container item id="last name" xs={12} md={6}>
                <Grid item xs={12}>
                  <Typography sx={theme.custom.styles.formLabelStyle}>Last Name</Typography>
                </Grid>
                <Grid item xs={12}>
                  <RegexTextField
                    size="small"
                    id="lastname"
                    value={lastName}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    regex={LAST_NAME_REGEX}
                    changeHandler={handleLastNameChange}
                    inputProps={{
                      step: 300, // 5 min
                    }}
                    sx={{ width: '100%' }}
                    variant="outlined"
                  />
                </Grid>
              </Grid>
            </Grid>
          )}
        </Grid>
        <Grid item xs={12}>
          <CallToAction
            nextButton={true}
            nextButtonDisabled={!driver || (showNameInput && (!firstName || !lastName))}
            nextButtonContent={continueBtn}
            handleNext={handleNext}
            backButton={true}
            backButtonContent={backBtn}
            handleBack={handleBack}
            skipButton={false}
            cancelButton={true}
            cancelButtonContent={cancelBtn}
            handleCancel={handleCancel}
          ></CallToAction>
        </Grid>
      </Grid>
    </Box>
  );
}
